import { Attachment } from "types/common.types";

import { UploadedFile } from "components/Common/FileUploader/index";
import { SelectOptions } from "components/Common/Select/index";

import { Role, User } from "store/auth/types";
import { MilestoneRequirement } from "store/milestones/types";
import { Task, TaskRequirement, TaskStatus } from "store/tasks/types";
import { Workspace } from "store/workspaces/types";

import { Guest } from "pages/Meetings/Form";

import { SingleVoting } from "./../voting/types";

export type PDFUserSigndFile = {
  isPdf: boolean;
  url: string;
  fileName: string;
};

export type PDFUsersAllowedToSignSummary = {
  files: PDFUserSigndFile[];
  id: number;
  name: string;
  signed: boolean;
  user_id: number;
  updated_at: string | null;
};

export type UsersAllowedToSignSummary = {
  files: Record<string, Attachment> | Attachment[];
  id: number;
  name: string;
  signed: boolean;
  is_current_turn_to_sign?: boolean;
  user_id: number;
  role?: Role;
  updated_at: string | null;
  signit_signatory_id?: string | null;
  signit_signature_request_id?: string | null;
};

export type SingleMeeting = {
  recording_url: string;
  recording_url_public: string;
  end_date: string;
  frequency: string;
  approvals: MeetingApproval[];
  frequency_days: string;
  id: number;
  name: string;
  quorum_type: string | null;
  repeat: boolean;
  start_date: string;
  type: string;
  participants: User[];
  status: MeetingStatus;
  reviewer: User;
  description: string;
  workspace: Workspace;
  private: boolean;
  agenda: Agenda;
  tasks: Task[];
  votings: SingleVoting[];
  creator: User;
  presence_of_quorum_required: boolean;
  meeting_address: string;
  reviewers: MeetingReviewer[];
  //TODO: check this
  guests: User[];
  meeting_minutes: MeetingMinutes;
  has_uploaded_summary: boolean;
  // Optional options for WS
  is_online?: boolean;
  content_counts?: {
    tasks: number;
    milestones: number;
    votings: number;
  };
  invitations_met_quorum?: boolean;
  started_at?: string;
  users_allowed_to_sign_summary?: UsersAllowedToSignSummary[];
  sent_to_review_at?: string | null;
  transcript: {
    content: string | null;
    highlights: string | null;
    key_points: string | null;
    summary: string | null;
    transcript_dialogue: transcriptDialogue[];
  };
};

export type transcriptDialogue = {
  speaker: string;
  text: string;
};

export type MeetingApproval = {
  id: number;
  created_at: string;
  reject_reason: string;
  status: string;
  user: User;
};

export type Agenda = {
  id: number;
  editable: boolean;
  creator: User;
  editor: User;
  sections: Section[];
};

export type MeetingReviewer = Omit<User, "status"> & {
  required?: boolean;
  last_approval_status: ReviewerApprovalStatus;
  status: ReviewerInvitationStatus & ReviewerApprovalStatus;
};

export enum ReviewerInvitationStatus {
  REQUESTES = "requested",
  PENDING = "pending",
}

export enum ReviewerApprovalStatus {
  ACCEPTED = "accepted",
  REQUIRED = "required",
  PENDING = "pending",
  SUBMITTED = "submitted",
  REJECTED = "rejected",
}

export type MeetingFormData = {
  id?: number;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  type: string | null;
  repeat: boolean;
  private: boolean;
  presence_of_quorum_required: boolean;
  frequency_days: string[] | null;
  frequency: string | null;
  meeting_address: string;
  quorum_type: string | null;
  users: { id: number; role: number }[];
  guests: Guest[];
  reviewers: number[];
  workspace_id?: number;
};

export enum MeetingUserRoles {
  Secretary = "secretary",
  Organiser = "organiser",
  Guest = "guest",
}

export type TwoStepAuthData = {
  service: string;
  destination: string;
};

export enum MeetingStatus {
  PendingApproval = "pending_approval",
  PendingReview = "pending_review",
  Approved = "approved",
  Draft = "draft",
  Started = "started",
  Ended = "ended",
  PendigSummarySign = "pending_summary_sign",
  Archived = "archived",
  PendingGenerate = "pending_summary_generate",
  SummarySigned = "summary_signed",
}

export enum AdminType {
  RootAdmin = "root_admin",
  SubAdmin = "sub_admin",
}

export enum ParticipantsStatus {
  NotRespondedYet = "not_responded_yet",
  Attending = "attending",
}

export type Section = {
  id: number;
  title: string;
  order: number;
  duration: number;
  description: string;
  speakers: User[];
  users: User[];
  subsections: Subsection[];
  status?: AgendaStatus;
  started_at?: string;
  ended_at?: string;
  attachments: Record<string, Attachment> | Attachment[];
  entry?: {
    id: number;
    notes: SectionNote[];
  };
};

export type FormSection = {
  id?: number;
  title: string;
  order: number;
  duration: number;
  description: string;
  speakers: number[];
  subsections: FormSubsection[];
};

export type SectionNote = {
  contents: string;
  contents_html: string;
  creator: User;
  id: number;
  tagged_users: User;
  type: string;
  locked: boolean;
  editor_id: number | null;
};

export type Subsection = {
  id: number;
  content: any;
  result_type?: "task" | "voting";
  type: "text" | "task" | "voting" | "milestone" | "result";
  title: string;
  order: number;
  duration: number;
  speakers: User[];
  status?: AgendaStatus;
  started_at?: string;
  ended_at?: string;
  attachments: Record<string, Attachment> | Attachment[];
  entry?: {
    id: number;
    notes: SectionNote[];
  };
};

export type FormSubsection = {
  id: number;
  content: any;
  type: string;
  title: string;
  order: number;
  duration: number;
  speakers: number[];
};

export type AgendaFormData = {
  id?: number;
  editable: boolean;
  editor: string;
  sections: FormSection[];
};

export type MeetingsFilters = Record<string, string>;

export type MeetingApprovalFormData = {
  status: "accepted" | "rejected";
  reason?: string;
};

export type LiveRoomAccessDetails = {
  sid: string;
  url: string;
  access_token: string;
};

export enum MeetingPresenceStatus {
  Attending = "attending",
  NotAttending = "not_attending",
  NotRespondedYet = "not_responded_yet",
}

export type MeetingPresenceFormData = {
  status: MeetingPresenceStatus;
  reason?: string;
};

export type AgendaSectionRequest = {
  agenda_id: number;
  order: number;
  title: string;
  description: string;
  duration?: number;
  speakers: number[];
};

export type AgendaSubsectionRequest = {
  id?: number;
  section: number;
  order: number;
  duration: number;
  type: string;
  content: number | string;
  speakers: number[];
};

export type MeetingSubsectionTextRequest = {
  id?: number;
  name: string;
  meeting_id: number;
  users: number[];
  description?: string;
};

export type MeetingSubsectionTextForm = {
  title: string;
  description: string;
  users: User[];
  speakers: User[];
  duration: number;
  files: UploadedFile[];
  serverFiles: Attachment[];
};

export type MeetingSubsectionResultsForm = {
  contentId?: number;
  users: User[];
  description: string;
  speakers: User[];
  duration: number;
  title: string;
  files: UploadedFile[];
  serverFiles: Attachment[];
};

export type MeetingResultsSubsection = {
  agenda_id: number;
  duration?: number;
};

export type MeetingResultsSubsectionRequest = {
  id?: number;
  content: number;
  name: string;
  meeting_id: number;
  users: number[];
  description?: string;
  type?: SubsectionTypes.VOTING | SubsectionTypes.TASK;
};

export type MeetingTaskSubsectionForm = {
  id?: number;
  title: string;
  description: string;
  priority: SelectOptions;
  files: UploadedFile[];
  requirements: TaskRequirement[];
  users: User[];
  status: TaskStatus;
  workspace: SelectOptions;
  speakers: User[];
  duration: number;
  serverFiles: Attachment[];
};

export type MeetingMilestoneSubsectionForm = {
  id?: number;
  title: string;
  users: User[];
  speakers: User[];
  duration: number;
  requirements: MilestoneRequirement[];
  files: UploadedFile[];
  serverFiles: Attachment[];
  description: string;
};

export enum AgendaStatus {
  Completed = "completed",
  Current = "current",
  NotStarted = "not_started",
}

export type MeetingMinutesRequest = {
  tagged_users: string[];
  contents: string;
  contents_html: string;
  type: string;
};

export enum SubsectionTypes {
  VOTING = "voting",
  TEXT = "text",
  TASK = "task",
  MILESTONE = "milestone",
  RESULT = "result",
}

export enum MeetingMinutesStatuses {
  PENDING_FOR_APPROVAL = "pending_approval",
  ONGOING = "ongoing",
  FINISHED = "finished",
  APPROVED = "approved",
  REJECTED = "rejected",
  ARCHIVED = "archived",
}

export type MeetingMinutes = {
  id: number;
  approved_at: string | null;
  conclusion: string | null;
  files: Attachment[];
  reviewers: Reviewer[];
  status: MeetingMinutesStatuses;
  submitted_by: User | null;
  entries: {
    id: number;
    notes: {
      contents: string;
      contents_html: string;
      creator: User;
      id: number;
      type: string;
    }[];
  }[];
};

export type Reviewer = {
  reject_reason: string | null;
  status: MeetingMinutesStatuses.APPROVED | MeetingMinutesStatuses.REJECTED;
  user: User;
  seen_at: string;
  updated_at: string;
};

export enum MeetingType {
  BY_PRESENCE = "by_presence",
  REMOTE = "remote",
}

export enum MeetingTypeValEN {
  by_presence = "By Presence",
  remote = "Remote",
}

export enum QuorumTypes {
  MAJORITY_NUMBER = "majority_number",
  MAJORITY_SHARES = "majority_shares",
  MAJOTIRTY_MIXED = "majority_mixed",
}

export type LiveParticipantType = {
  userId: string;
  connectionId: string;
};

// export type SubsectionContent = {
//   description: string;
//   id: number;
//   is_moved_to_workspace: boolean;
//   name: string;
// };
